import { skipToken } from '@reduxjs/toolkit/query';
import { booleanPointInPolygon } from '@turf/turf';
import { CantonAbbr } from 'domain/types/location/cantons';
import { lv95ToWgs, wgsToLV95 } from 'domain/utils/coordinates';
import { Position } from 'geojson';
import useMergedPlotIds from 'pages/App/Explore/hooks/useMergedPlotIds';
import { useMemo } from 'react';
import { useGetPlotByPointQueryState } from 'state/services/backend/endpoints/map/cadastre';
import { ProjectLocationBaseData } from 'state/services/backend/types/project';
import { useGetAddressByPointQuery } from 'state/services/geoAdmin/api';
import { Nullable } from 'types/utils';
import { getCenter } from 'utils/gis';

const useEgrids = (merge?: (string | null)[] | null, egrid?: string) => {
  return useMemo(() => {
    if (merge && merge.length > 0) return merge.filter(Boolean) as string[];
    if (egrid) return [egrid];

    return [];
  }, [merge, egrid]);
};

interface UseExploreLocationDataProps {
  lng: Nullable<number>;
  lat: Nullable<number>;
}

const useExploreLocationData = (props: UseExploreLocationDataProps) => {
  const { lng, lat } = props;

  const hasLngLat = typeof lng === 'number' && !isNaN(lng) && typeof lat === 'number' && !isNaN(lat);

  const { mergedPlotIds } = useMergedPlotIds();

  const {
    data: getPlotByPointData,
    error,
    isLoading: isPlotLoading,
    isFetching: isPlotFetching,
  } = useGetPlotByPointQueryState(hasLngLat ? { lng: lng, lat: lat } : skipToken);

  const feature = getPlotByPointData?.features?.length ? getPlotByPointData.features[0] : undefined;
  const plotPolygonCenter = feature?.geometry?.coordinates ? getCenter(feature.geometry.coordinates) : undefined;

  const plotCenter = { lng: plotPolygonCenter?.lng ?? lng ?? 0, lat: plotPolygonCenter?.lat ?? lat ?? 0 };

  const plotId = feature?.id ? Number(feature.id) : 0;

  const { address } = useGetAddressByPointQuery(hasLngLat ? plotCenter : skipToken, {
    selectFromResult: ({ data, originalArgs }) => {
      const { lng, lat } = originalArgs ?? {};

      if (!data || !feature || !lng || !lat) {
        return {
          address: '',
        };
      }

      const results = data.results;

      if (!results)
        return {
          address: '',
        };

      const { x: centerX, y: centerY } = wgsToLV95({ lng, lat });

      const filteredResults = results.filter((res) => {
        const { geometry } = res;
        const { lng, lat } = lv95ToWgs(geometry);
        return feature.geometry && booleanPointInPolygon([lng, lat], feature.geometry);
      });

      let address = '';
      let shortestDistance = Infinity;

      filteredResults.forEach((res) => {
        const { x, y } = res.geometry;
        const resultDistance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
        if (resultDistance < shortestDistance) {
          shortestDistance = resultDistance;
          address = `${res.attributes.label}, ${res.attributes.dplz4} ${res.attributes.dplzname}`;
        }
      });

      if (address === '') {
        results.forEach((result) => {
          const { x, y } = result.geometry;
          const resultDistance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
          if (resultDistance < shortestDistance) {
            shortestDistance = resultDistance;
            address = `${result.attributes.label}, ${result.attributes.dplz4} ${result.attributes.dplzname}`;
          }
        });
      }

      return { address };
    },
  });

  const egrids = useEgrids(mergedPlotIds, feature?.properties?.egrid);

  const polygon = feature?.geometry.coordinates;

  const { area = 0, bfsnr = 0, cantonAbbr, municipality = '', egrid = '', plotNumber = '' } = feature?.properties ?? {};

  const location: ProjectLocationBaseData = {
    bfsnr,
    municipality,
    plotCenter,
    egrids,
    plot: {
      id: plotId,
      plotNumber,
      egrid,
      plotArea: area,
      // TODO: Remove `as` when possible
      polygon: polygon as Position[][] | Position[][][],
    },
    address,
    canton: cantonAbbr as CantonAbbr,
  };

  return {
    address,
    egrids,
    error,
    plotNumber,
    hasLocationData: !!getPlotByPointData,
    isPlotFetching,
    isPlotLoading,
    lat,
    lng,
    location,
    plotId,
    uniquePlots: [
      {
        plotNumber,
        bfsnr,
      },
    ],
  };
};

export default useExploreLocationData;
